import { Target, Zap, Eye, DollarSign, Shield, TrendingUp, ChevronRight, Sparkles, AlertTriangle, CheckCircle, CircleDot, AlertCircle, MessageSquare } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';

interface StructuredHLO {
  id: string;
  title: string;
  description: string;
  category: string;
  source: string;
  status: string;
}

interface PainPoint {
  id?: string;
  text: string;
  category?: string;
}

interface HloCoverageEntry {
  hloId: string;
  title: string;
  category: string;
  coverage: 'covered' | 'partial' | 'uncovered';
  matchedScopeItems: Array<{ scopeItemId: string; category: string; description: string; matchType: string }>;
  gaps: string[];
}

interface HloAlignmentResult {
  score: number;
  hloCoverage: HloCoverageEntry[];
  summary: {
    covered: number;
    partial: number;
    uncovered: number;
    total: number;
  };
  computedAt: string;
  engagementId: string;
}

interface StrategicObjectivesProps {
  hlos: string[];
  structuredHLOs: StructuredHLO[];
  painPoints: PainPoint[];
  engagementId?: string;
  hloAlignment?: HloAlignmentResult | null;
  onRefineObjectives?: () => void;
  onAddContext?: () => void;
  onContactConsultant?: () => void;
}

// Derive focus tags from HLO categories and text
function deriveFocusTags(hlos: string[], structuredHLOs: StructuredHLO[]): string[] {
  const tags = new Set<string>();
  const keywords: Record<string, string[]> = {
    'Speed': ['speed', 'fast', 'quick', 'rapid', 'accelerat', 'time', 'sla', 'response'],
    'Efficiency': ['efficien', 'streamlin', 'automat', 'optimi', 'reduc', 'workflow'],
    'Visibility': ['visib', 'report', 'dashboard', 'insight', 'monitor', 'metric', 'track'],
    'Cost Optimization': ['cost', 'budget', 'spend', 'saving', 'roi', 'expense'],
    'Compliance': ['compli', 'regulat', 'audit', 'govern', 'policy', 'standard'],
    'Scalability': ['scal', 'grow', 'expand', 'capacity', 'volume'],
  };

  // Check structured HLO categories
  for (const hlo of structuredHLOs) {
    if (hlo.category === 'efficiency' || hlo.category === 'automation') tags.add('Efficiency');
    if (hlo.category === 'reporting') tags.add('Visibility');
    if (hlo.category === 'compliance') tags.add('Compliance');
    if (hlo.category === 'user_experience') tags.add('Speed');
    if (hlo.category === 'integration') tags.add('Scalability');
  }

  // Keyword match on all HLO text
  const allText = [...hlos, ...structuredHLOs.map(h => `${h.title} ${h.description}`)].join(' ').toLowerCase();
  for (const [tag, kws] of Object.entries(keywords)) {
    if (kws.some(kw => allText.includes(kw))) {
      tags.add(tag);
    }
  }

  // Always return at least 2 tags
  if (tags.size === 0) {
    tags.add('Efficiency');
    tags.add('Speed');
  }

  return Array.from(tags).slice(0, 5);
}

// Group pain points into categories
function groupPainPoints(painPoints: PainPoint[]): Record<string, PainPoint[]> {
  const groups: Record<string, PainPoint[]> = {};

  const categoryKeywords: Record<string, string[]> = {
    'Service Experience': ['service', 'customer', 'user', 'experience', 'support', 'ticket', 'request', 'response', 'sla', 'satisfaction'],
    'Operational Efficiency': ['manual', 'process', 'workflow', 'time', 'effort', 'automat', 'efficien', 'slow', 'bottleneck', 'delay'],
    'Visibility & Insight': ['report', 'dashboard', 'visibility', 'metric', 'data', 'insight', 'monitor', 'track', 'analytic'],
    'Cost & Scalability': ['cost', 'budget', 'scale', 'grow', 'resource', 'capacity', 'spend', 'headcount'],
  };

  for (const pp of painPoints) {
    const text = (pp.text || '').toLowerCase();
    let matched = false;

    if (pp.category && groups[pp.category] === undefined) {
      groups[pp.category] = [];
    }

    if (pp.category) {
      groups[pp.category].push(pp);
      matched = true;
    } else {
      for (const [cat, kws] of Object.entries(categoryKeywords)) {
        if (kws.some(kw => text.includes(kw))) {
          if (!groups[cat]) groups[cat] = [];
          groups[cat].push(pp);
          matched = true;
          break;
        }
      }
    }

    if (!matched) {
      if (!groups['Other']) groups['Other'] = [];
      groups['Other'].push(pp);
    }
  }

  return groups;
}

const tagIcons: Record<string, any> = {
  'Speed': Zap,
  'Efficiency': TrendingUp,
  'Visibility': Eye,
  'Cost Optimization': DollarSign,
  'Compliance': Shield,
  'Scalability': TrendingUp,
};

const tagColors: Record<string, string> = {
  'Speed': 'bg-amber-50 text-amber-700 border-amber-200',
  'Efficiency': 'bg-blue-50 text-blue-700 border-blue-200',
  'Visibility': 'bg-purple-50 text-purple-700 border-purple-200',
  'Cost Optimization': 'bg-green-50 text-green-700 border-green-200',
  'Compliance': 'bg-red-50 text-red-700 border-red-200',
  'Scalability': 'bg-teal-50 text-teal-700 border-teal-200',
};

const alignmentMappings = [
  { label: 'SLA Calendar', objective: 'SLA consistency & response targets' },
  { label: 'HLO Refinement', objective: 'Clarify & validate objectives' },
  { label: 'Architecture', objective: 'Scalability & structure' },
];

export function StrategicObjectives({
  hlos,
  structuredHLOs,
  painPoints,
  hloAlignment,
  onRefineObjectives,
  onAddContext,
  onContactConsultant,
}: StrategicObjectivesProps) {

  const hasData = structuredHLOs.length > 0 || hlos.length > 0;
  const hasPainPoints = painPoints.length > 0;
  const focusTags = deriveFocusTags(hlos, structuredHLOs);
  const groupedPainPoints = groupPainPoints(painPoints);

  // Empty state
  if (!hasData && !hasPainPoints) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
        <div className="flex flex-col items-center text-center max-w-md mx-auto">
          <div className="h-14 w-14 rounded-full bg-indigo-50 flex items-center justify-center mb-4">
            <Target className="size-7 text-indigo-400" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">No objectives defined yet.</h2>
          <p className="text-sm text-gray-500 mb-6">
            Start by defining your goals. Strategic objectives guide every decision in your implementation plan.
          </p>
          {onRefineObjectives && (
            <Button onClick={onRefineObjectives} className="bg-indigo-600 hover:bg-indigo-700 text-white">
              <Sparkles className="size-4 mr-2" />
              Start
            </Button>
          )}
        </div>
      </div>
    );
  }

  // Determine display HLOs - prefer structured, fallback to string
  const displayHLOs = structuredHLOs.length > 0
    ? structuredHLOs
    : hlos.map((h, i) => ({ id: `legacy_${i}`, title: h, description: '', category: 'other', source: 'legacy', status: 'approved' }));

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      {/* HEADER */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Target className="size-5 text-indigo-600" />
            Your Strategic Objectives
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Based on your onboarding inputs, these are the outcomes guiding your implementation.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {focusTags.map(tag => {
            const Icon = tagIcons[tag] || Target;
            const color = tagColors[tag] || 'bg-gray-50 text-gray-700 border-gray-200';
            return (
              <Badge key={tag} variant="outline" className={`${color} px-3 py-1 text-sm font-medium`}>
                <Icon className="size-3.5 mr-1.5" />
                {tag}
              </Badge>
            );
          })}
        </div>
      </div>

      {/* TWO-COLUMN LAYOUT */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* LEFT COLUMN (2/3) - Primary Content */}
        <div className="lg:col-span-2 space-y-6">

          {/* SECTION 1: Desired Outcomes */}
          <div>
            <h3 className="text-base font-semibold text-gray-800 mb-1">Desired Outcomes</h3>
            <p className="text-sm text-gray-500 mb-3">Clear goals this engagement is designed to achieve</p>
            <div className="space-y-2.5">
              {displayHLOs.map(hlo => (
                <div key={hlo.id} className="flex items-start gap-3 p-3 rounded-lg bg-indigo-50/50 border border-indigo-100">
                  <div className="size-2 rounded-full bg-indigo-500 mt-2 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-800">{hlo.title}</p>
                    {hlo.description && (
                      <p className="text-xs text-gray-500 mt-0.5">{hlo.description}</p>
                    )}
                  </div>
                  {hlo.status === 'approved' && (
                    <Badge variant="outline" className="ml-auto text-[10px] shrink-0 bg-green-50 text-green-700 border-green-200">
                      Approved
                    </Badge>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* SECTION 2: Current Challenges (Pain Points) */}
          {hasPainPoints && (
            <div>
              <h3 className="text-base font-semibold text-gray-800 mb-1">Current Challenges</h3>
              <p className="text-sm text-gray-500 mb-3">Key issues identified during onboarding</p>
              <div className="space-y-4">
                {Object.entries(groupedPainPoints).map(([category, points]) => (
                  <div key={category}>
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="size-3.5 text-amber-500" />
                      <h4 className="text-sm font-medium text-gray-700">{category}</h4>
                      <Badge variant="outline" className="text-[10px] text-gray-500">{points.length}</Badge>
                    </div>
                    <ul className="space-y-1.5 ml-6">
                      {points.slice(0, 5).map((pp, i) => (
                        <li key={pp.id || `pp_${i}`} className="text-sm text-gray-600 flex items-start gap-2">
                          <span className="text-gray-400 mt-1 shrink-0">&bull;</span>
                          <span>{pp.text}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* RIGHT COLUMN (1/3) - Context */}
        <div className="space-y-5">

          {/* Engagement Focus */}
          <Card className="border-gray-200 shadow-none">
            <CardContent className="pt-5 pb-4 px-5">
              <h4 className="text-sm font-semibold text-gray-800 mb-3">Engagement Focus</h4>
              <div className="flex flex-wrap gap-2">
                {focusTags.map(tag => {
                  const color = tagColors[tag] || 'bg-gray-50 text-gray-700 border-gray-200';
                  return (
                    <Badge key={tag} variant="outline" className={`${color} text-xs px-2 py-0.5`}>
                      {tag}
                    </Badge>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Alignment to Contracted Scope (TASK 4) */}
          {hloAlignment && (
            <Card className={`shadow-none ${
              hloAlignment.summary.uncovered > 0 
                ? 'border-amber-300 bg-amber-50/30' 
                : hloAlignment.score >= 80 
                  ? 'border-green-200 bg-green-50/30' 
                  : 'border-gray-200'
            }`}>
              <CardContent className="pt-5 pb-4 px-5">
                <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <Target className="size-3.5 text-indigo-500" />
                  Alignment to Contracted Scope
                </h4>

                {/* Score Bar */}
                <div className="mb-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-500">Coverage</span>
                    <span className={`text-sm font-bold ${
                      hloAlignment.score >= 80 ? 'text-green-700' : hloAlignment.score >= 50 ? 'text-amber-700' : 'text-red-700'
                    }`}>{hloAlignment.score}%</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${
                        hloAlignment.score >= 80 ? 'bg-green-500' : hloAlignment.score >= 50 ? 'bg-amber-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${hloAlignment.score}%` }}
                    />
                  </div>
                </div>

                {/* Counts */}
                <div className="flex gap-3 text-xs mb-3">
                  <div className="flex items-center gap-1">
                    <CheckCircle className="size-3 text-green-600" />
                    <span className="text-gray-600">{hloAlignment.summary.covered} covered</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <CircleDot className="size-3 text-amber-500" />
                    <span className="text-gray-600">{hloAlignment.summary.partial} partial</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <AlertCircle className="size-3 text-red-500" />
                    <span className="text-gray-600">{hloAlignment.summary.uncovered} uncovered</span>
                  </div>
                </div>

                {/* Per-HLO breakdown */}
                <div className="space-y-1.5">
                  {hloAlignment.hloCoverage.map(entry => (
                    <div key={entry.hloId} className="flex items-center gap-2 text-xs">
                      {entry.coverage === 'covered' && <CheckCircle className="size-3 text-green-600 shrink-0" />}
                      {entry.coverage === 'partial' && <CircleDot className="size-3 text-amber-500 shrink-0" />}
                      {entry.coverage === 'uncovered' && <AlertCircle className="size-3 text-red-500 shrink-0" />}
                      <span className={`truncate ${entry.coverage === 'uncovered' ? 'text-red-700 font-medium' : 'text-gray-600'}`}>
                        {entry.title}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Watchouts — uncovered HLOs (TASK 5) */}
          {hloAlignment && hloAlignment.summary.uncovered > 0 && (
            <Card className="border-red-200 bg-red-50/40 shadow-none">
              <CardContent className="pt-5 pb-4 px-5">
                <h4 className="text-sm font-semibold text-red-800 mb-2 flex items-center gap-2">
                  <AlertTriangle className="size-3.5 text-red-600" />
                  Watchouts
                </h4>
                <p className="text-xs text-red-700 mb-3">
                  Some of your goals are not covered in the current engagement scope.
                </p>
                <ul className="space-y-1.5 mb-3">
                  {hloAlignment.hloCoverage
                    .filter(e => e.coverage === 'uncovered')
                    .map(e => (
                      <li key={e.hloId} className="text-xs text-red-700 flex items-start gap-2">
                        <AlertCircle className="size-3 text-red-500 mt-0.5 shrink-0" />
                        <span>{e.title}</span>
                      </li>
                    ))}
                </ul>
                {onContactConsultant && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full text-xs border-red-300 text-red-700 hover:bg-red-100"
                    onClick={onContactConsultant}
                  >
                    <MessageSquare className="size-3 mr-1.5" />
                    Review with Consultant
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          {/* Alignment to Plan (static mappings) */}
          <Card className="border-gray-200 shadow-none">
            <CardContent className="pt-5 pb-4 px-5">
              <h4 className="text-sm font-semibold text-gray-800 mb-3">Alignment to Plan</h4>
              <div className="space-y-3">
                {alignmentMappings.map(mapping => (
                  <div key={mapping.label} className="flex items-center gap-2 text-xs">
                    <ChevronRight className="size-3 text-indigo-400 shrink-0" />
                    <span className="font-medium text-gray-700">{mapping.label}</span>
                    <span className="text-gray-400">&rarr;</span>
                    <span className="text-gray-500">{mapping.objective}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* CTAs */}
          <div className="space-y-2">
            {onRefineObjectives && (
              <Button
                onClick={onRefineObjectives}
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                Refine Objectives
              </Button>
            )}
            {onAddContext && (
              <Button
                variant="outline"
                onClick={onAddContext}
                className="w-full"
              >
                Add More Context
              </Button>
            )}
            {onContactConsultant && (
              <Button
                variant="outline"
                onClick={onContactConsultant}
                className="w-full"
              >
                Contact Consultant
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}